/*
 * @Description:
 * @Date: 2023-12-04 17:11:31
 */
import { ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { XrkMessage, XrkMessageBox } from 'xrk-components';

import { isString } from '@/utils/is';
import { save, get } from '../data-source/apis';
import { ModelType, SCENE_MAPPING } from '../constants';
import { checkValueDone } from '../hooks';
import { getBaseMenus, MenuProps } from '../hooks/use-get-menus';

function rgbaMixWhiteToRgb(hex: string, opacity: number) {
  const redValue = parseInt('0x' + hex.slice(1, 3));
  const greenValue = parseInt('0x' + hex.slice(3, 5));
  const blueValue = parseInt('0x' + hex.slice(5, 7));
  const alpha = 1 - opacity;

  // 计算新颜色的RGBA值（将原始颜色的RGB值与白色进行线性混合）
  return `rgb(${[
    Math.round(redValue * (1 - alpha) + 255 * alpha),
    Math.round(greenValue * (1 - alpha) + 255 * alpha),
    Math.round(blueValue * (1 - alpha) + 255 * alpha)
  ].join(',')})`;
}

const DEFAULT_SCENE_TYPE = 20;

const name = 'useBiTmplDetailStoreSetup';
const useBiTmplDetailSteup = defineStore(name, () => {
  const baseMenus = ref(null);
  const getMenus = (arr): Map<string, MenuProps> => {
    return new Map(baseMenus.value.filter(item => arr.includes(item[0])));
  };
  const state = ref(null);
  const initState = () => {
    baseMenus.value = getBaseMenus();
    state.value = {
      /* 
        当前激活菜单key，目前只有项目执行人存在二级菜单。
        设置、获取菜单都通过该变量。
    */
      activeMenu: ['101', ''] as [string, string],
      /* 
        使用Map，方便管理菜单渲染顺序，通过key-1判断上一个是否设置完毕，当前未设置完毕则不可设置下一个。
        菜单顺序与是否展示过该值控制。
        右侧设置选择类型值与是否展示。
    */
      menuValue: getMenus(SCENE_MAPPING[DEFAULT_SCENE_TYPE].content),
      curMenu: {} as any,
      sceneType: DEFAULT_SCENE_TYPE, // 场景类型:5-调研问卷,20-可配置场景；必填项
      templateName: '', // 模板名称
      id: undefined, // 模板ID
      topicType: 1 // 主题类型（同样式主题色“BASIC_STYLE_COLOR”的取值），保存时同步，后端要用，新版BI没用
    };
  };
  initState();

  // 获取当前菜单，优先返回二级菜单
  const getLevelMenu = () => {
    const levels = state.value.activeMenu;
    let menu = state.value.menuValue.get(levels[0]);
    if (levels[1]) {
      menu = menu.value[levels[1]];
    }
    return menu;
  };
  // 设置当前菜单，进入时必定有默认值
  const setActiveMenu = (menuKey, menuLevel = 0) => {
    state.value.activeMenu[menuLevel] = menuKey;
    // 切换一级时重置二级
    if (menuLevel === 0) {
      state.value.activeMenu[1] = '';
    }
  };
  /**
   * @description: 设置当前项，包含子项
   * @param {*} v 选择的值
   * @param {*} childKey 子项key
   * @return {void}
   */
  const setMenuValue = (val, childKey?) => {
    const menu = getLevelMenu();
    console.log(val, childKey, menu, 'val, childKey');

    if (childKey) {
      menu.value[childKey].value = val;
    } else {
      menu.value = val;
    }
  };
  /**
   * @description: 设置菜单显示，包含子项
   * @param {*} v 选择的值
   * @param {*} childKey 子项key
   * @return {void}
   */
  const setMenuShow = (menu, v, childKey?) => {
    const _menu = menu || getLevelMenu();
    if (childKey) {
      _menu.value[childKey].show = v;

      if (
        Object.prototype.toString.call(_menu.value[childKey].value) ===
        '[object Object]'
      ) {
        const keys = Object.keys(_menu.value[childKey].value);
        if (!_menu.value[childKey].value[keys[0]]?.modelType) return;

        keys.forEach(key => {
          _menu.value[childKey].value[key].show = v;
        });
      }
    } else {
      _menu.show = v;
    }
  };
  // 可配置有实例展示模块，调研无该模块
  const setExampleModule = () => {
    state.value.menuValue = getMenus(
      SCENE_MAPPING[state.value.sceneType].content
    );
  };
  /**
   * @description: 获取当前需要设置项
   * @param {*} childKey 存在则获取子项，不存在则获取父项
   * @return {*} menu
   */
  const getCurSetItem = (childKey?) => {
    const menu = getLevelMenu();
    if (childKey) {
      const m = menu.value[childKey];
      state.value.curMenu = m;
      return m;
    }
    state.value.curMenu = menu;
    return menu;
  };
  const saveTmpl = async () => {
    /*   
     保存数据示例
    {
      "id": 1, //模板ID：新增时为null，修改时必填
      "templateName": "模板名称", //必填项
      "sceneType": 5, //场景类型:5-调研问卷,20-可配置场景；必填项
      "topicType": 1, //主题类型（同样式主题色“BASIC_STYLE_COLOR”的取值），必填项
      "modules": [//组件模块
          {
              "modelType": "BASIC_STYLE",//模块类型，必填项，详见“模块类型说明”
              "isShow": 1, //是否展示：1-展示，0-不展示；必填项
              "styleList": [//模块对应的样式列表
                  {
                      "styleKey": "BASIC_STYLE_COLOR", //样式名称，必填，一个模块下只有一个样式的时候取值"DEFAULT"，详见“样式名称说明”
                      "styleCode": "1", //样式取值，由前端自行确定
                      "isShow": 1,//是否显示，选填，默认取1，水印是否显示、卡方附件是否显示等可用此字段
                      "content": "河南薪未来科技有限公司" //选填，样式中涉及自定义内容的时候可使用此字段，比如水印选择公司名称时
                  }
              ]
          },
          {
            modelType:'PROJECT_EXE_PROGRESS',
            isShow:1,
            styleList:[
              {
                styleKey:'DEFAULT',
                styleCode:1,
                isShow:1,
                content:''
              }
            ]
          }
      ]
    } 
  */
    const modules = []; // 模块组装集合
    const formatData = (data, level) => {
      const obj: Record<any, any> = {
        isShow: +data.show,
        _isDone: true // 默认true设置完成，false未完成。仅判断了styleCode，未判断show
      };
      // 设置模块标识字段，1级使用modelType 2级使用styleKey
      if (level === 1) {
        obj.modelType = data.modelType;
      }
      if (level === 2) {
        obj.styleKey = data.modelType;
      }

      // 最后一层
      if (isString(data.value)) {
        const commonObj = {
          styleCode: data.value,
          isShow: +data.show,
          content: data.content
        };
        // 保存时同步topicType
        if (obj.styleKey === '101_1') {
          state.value.topicType = commonObj.styleCode;
        }
        // 有异议，设为不显示是否算到已设置中？
        // 给未设置添加标记
        if (!commonObj.styleCode) {
          // 水印特殊处理，不显示也极为完成
          if (obj.styleKey === '101_5' && !commonObj.isShow) {
            obj._isDone = true;
          } else {
            obj._isDone = false;
          }
        }
        /* 
          1级：如果没有子集则向styleList中添加一个DEFAULT子集
          所有的1级模块都必须有一个styleList子集。在此处直接输出包含styleList的对象
        */
        if (level === 1) {
          obj.styleList = [
            {
              styleKey: 'DEFAULT',
              ...commonObj
            }
          ];
        }
        // 设置2级值，由else中的遍历push到styleList中。此处输出对象，不带有styleList
        if (level === 2) {
          Object.assign(obj, commonObj);
        }
      } else {
        // 此判断是为了解决，保存时百分比分析和中位数没有值时报错
        if (data.value) {
          // 含有多个子集，添加styleList字段并递归生成子集字段
          obj.styleList = [];
          Object.values(data.value).forEach(item => {
            const itemData = formatData(item, 2);
            if (!itemData._isDone) {
              obj._isDone = false;
            }
            obj.styleList.push(itemData);
          });
        }
      }
      return obj;
    };
    // 遍历格式数据
    state.value.menuValue.forEach(item => {
      if (!item.renderChild) {
        modules.push(formatData(item, 1));
      } else {
        Object.values(item.value).forEach(bItem => {
          modules.push(formatData(bItem, 1));
        });
      }
    });

    const NOT_PARTICIPATING = ['EXAMPLE_SHARING', 'PROJECT_EXE_FLOW', 'COVER']; // 不参与完成校验的模块

    // 已设置集合。模版设置最少需要选择 5 个模块，不包含封面，才可以保存
    if (
      modules.filter(
        item => !NOT_PARTICIPATING.includes(item.modelType) && item.isShow
      ).length < 5
    ) {
      XrkMessage.error('请设置至少5个模块');
      return Promise.reject();
    }

    if (
      (() => {
        const res = [];
        state.value.menuValue.forEach(item => res.push(item));
        return res;
      })().filter(item => {
        return checkValueDone(item) == 0;
      }).length > 0
    ) {
      XrkMessage.error('请配置展示模块内容');
      return Promise.reject();
    }
    if (!state.value.templateName) {
      XrkMessage.error('请填写模板名称');
      return Promise.reject();
    }
    await XrkMessageBox.confirm('是否确认保存？');
    await save({
      sceneType: state.value.sceneType, // 场景类型:5-调研问卷,20-可配置场景；必填项
      templateName: state.value.templateName, // 模板名称
      ...(state.value.id && { id: state.value.id }),
      topicType: state.value.topicType,
      modules
    });
  };
  /**
   * @description: 初始数据
   * @param {number} templateId 如果存在则通过接口拉取
   * @return {void}
   */
  const initTmpl = async (templateId, baseSceneType?, incomingData?, skip?) => {
    initState();
    if (!templateId) {
      state.value.sceneType = +baseSceneType;
      return;
    }

    let data = {
      modules: [],
      templateName: '',
      sceneType: null,
      topicType: '',
      id: null
    };
    if (!incomingData) {
      const res = await get({ templateId, skip });
      data = res.data;
    } else {
      // 外部传入数据
      data = incomingData;
    }

    const { modules, templateName, sceneType, topicType, id } = data;

    state.value.templateName = templateName;
    state.value.sceneType = sceneType;
    state.value.topicType = topicType;
    setExampleModule();
    // 只有编辑需要 id
    if (window.location.hash.includes('type=edit')) state.value.id = id;

    // 有子级的菜单特殊处理
    const specialMenuHandlers = Array.from(state.value.menuValue.values())
      .filter((item: any) => item?.rootType)
      .reduce((obj: any, item: any) => {
        return {
          ...obj,
          [item.rootType]: item.defaultChildKey.split('_')[0]
        };
      }, {});
    modules.forEach(item => {
      let menu: any;

      // 检查是否是特殊处理的模块类型
      const specialMenuKey = Object.keys(specialMenuHandlers).find(key =>
        item.modelType.includes(key)
      );
      if (specialMenuKey) {
        menu = state.value.menuValue.get(specialMenuHandlers[specialMenuKey])
          .value[ModelType[item.modelType]];
      } else {
        menu = state.value.menuValue.get(ModelType[item.modelType]);
      }
      if (!menu) return;

      menu.show = Boolean(item.isShow);
      item.styleList.forEach(bItem => {
        if (bItem.styleKey === 'DEFAULT') {
          menu.value = bItem.styleCode;
        } else {
          menu.value[ModelType[bItem.styleKey]].value = bItem.styleCode;
          menu.value[ModelType[bItem.styleKey]].show = Boolean(bItem.isShow);
        }
      });
    });

    // 检查并更新特殊菜单的显示状态
    Object.values(specialMenuHandlers).forEach(menuKey => {
      const menu = state.value.menuValue.get(menuKey);
      if (menu && menu.value) {
        menu.show = Object.values(menu.value).some((i: any) => i.show);
      }
    });
  };
  // 场景切换，对应显示的菜单
  watch(
    () => state.value.sceneType,
    () => {
      if (state.value.sceneType) setExampleModule();
    },
    {
      immediate: true,
      deep: true
    }
  );
  // 监听主题色变化
  watch(
    () => state.value.menuValue.get('101'),
    v => {
      const curValue = v.value;
      const root = document.documentElement;
      // 获取主题色
      const value = curValue['101_1'].value;
      if (!value.length) {
        // 没颜色先初始一个黑的
        root.style.setProperty(`--theme-color-1`, '#000');
        return;
      }

      const _v = curValue['101_1'].value.split(',');
      _v.forEach((color, index) => {
        root.style.setProperty(`--theme-color-${index + 1}`, color);
        for (let x = 1; x <= 10; x++) {
          root.style.setProperty(
            `--theme-color-${index + 1}-${parseInt(x * 10)}`,
            rgbaMixWhiteToRgb(color, x / 10)
          );
        }
      });
    },
    {
      immediate: true,
      deep: true
    }
  );
  return {
    state,
    setActiveMenu,
    setMenuValue,
    setMenuShow,
    getCurSetItem,
    saveTmpl,
    initTmpl
  };
});

export { useBiTmplDetailSteup, name };
